import { Button, Input, Select, Space, Switch, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { Link } from "react-router-dom";
import { keycloak } from "../../..";
import { useAppSelector } from "../../../app/store";
import AddIcon from "../../../assets/icons/AddIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import CustomTable from "../../../components/ui/CustomTable";
import {
  useGetAllCompanyQuery,
  useUpdateCompanyMutation,
} from "../../../features/portal/companyOnboard/companyOnboardApi";
import { API_URL } from "../../../constants/apiUrl";
import { APP_API } from "../../../constants/constants";
import useError from "../../../hooks/useError";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import {
  GetAllCompanyDataParamsType,
  GetAllCompanyDataType,
} from "../../../types/portal/companyOnboard";
import { capitalize } from "../../../utils/string";
import { addKeyInArray } from "../../../utils/utils";

export default function BusinessList() {
  type CompanyRow = GetAllCompanyDataType["data"]["companies"][number];
  const [statusUpdateId, setStatusUpdateId] = useState<number | null>(null);
  const {
    page,
    limit,
    handlePagination,
    handleFilter,
    handleLimit,
    filter,
    keyword,
    handleSearch,
  } = useTable<GetAllCompanyDataParamsType>();
  const { role, access_token } = useAppSelector((state) => state.auth);
  const isSuperAdmin =
    role === "SUPER_ADMIN" ||
    keycloak?.realmAccess?.roles?.includes("super_admin");
  const { isError, error, isLoading, data } = useGetAllCompanyQuery({
    params: {
      ...filter,
      search: keyword,
      skip: undefined,
      page,
      // company_id: user?.data?.company_id,
    },
  });
  useError(isError, error, "Get all company fail");
  const [updateCompany, { isLoading: isStatusUpdateLoading }] =
    useUpdateCompanyMutation();

  const getPublishedStatus = (company: CompanyRow) => {
    if (typeof company?.is_published === "number") return company.is_published === 1;
    if (typeof company?.is_published === "boolean") return company.is_published;
    return company?.status?.toLowerCase() !== "unpublished";
  };

  const handleBusinessPublishStatus = async (
    company: CompanyRow,
    checked: boolean
  ) => {
    try {
      setStatusUpdateId(company.id);
      const response = await fetch(
        `${APP_API}${API_URL.getSingleCompany}/${company.id}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${access_token}`,
            "ngrok-skip-browser-warning": "true",
          },
        }
      );
      const detailsResponse = await response.json();
      const details = detailsResponse?.data;
      const hasPublishField =
        details &&
        (Object.prototype.hasOwnProperty.call(details, "is_published") ||
          Object.prototype.hasOwnProperty.call(details, "status"));

      if (!hasPublishField) {
        toast(
          "warning",
          "Publish/Unpublish is not supported by backend yet for company."
        );
        return;
      }

      const payload = {
        logo: details?.logo,
        company_name: details?.company_name,
        ...(isSuperAdmin ? { business_email: details?.business_email } : {}),
        mobile_number: details?.mobile_number,
        location: details?.location,
        address: details?.address,
        tin_certificate_file_link: details?.tin_certificate_file_link,
        tin_certificate_issue_date: details?.tin_certificate_issue_date,
        tin_certificate_expire_date: details?.tin_certificate_expire_date,
        bank_name: details?.bank_name,
        bank_account_number: details?.bank_account_number,
        branch_name: details?.branch_name,
        can_view_all_products: details?.can_view_all_products,
        dynamicKeys:
          details?.dynamicKeys?.map((item: any) => ({
            dynamic_key_name: item.dynamic_key_name,
            dynamic_id: item.dynamic_id,
            dynamic_keyword: item.dynamic_keyword,
          })) ?? null,
        ...(Object.prototype.hasOwnProperty.call(details, "is_published")
          ? { is_published: checked ? 1 : 0 }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(details, "status")
          ? { status: checked ? "published" : "unpublished" }
          : {}),
      };

      const sanitizedPayload = Object.fromEntries(
        Object.entries(payload).filter(([_, value]) => value !== undefined)
      );

      await updateCompany({
        id: String(company.id),
        data: sanitizedPayload,
      }).unwrap();

      const verifyRes = await fetch(
        `${APP_API}${API_URL.getSingleCompany}/${company.id}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${access_token}`,
            "ngrok-skip-browser-warning": "true",
          },
        }
      );
      const verifyJson = await verifyRes.json();
      const updated = verifyJson?.data;
      const applied = getPublishedStatus(updated);

      if (applied === checked) {
        toast(
          "success",
          `Business ${checked ? "published" : "unpublished"} successfully`
        );
      } else {
        toast(
          "warning",
          "Update request succeeded but publish status did not change. Backend support is missing."
        );
      }
    } catch (err: any) {
      const issues = err?.data?.message;
      if (Array.isArray(issues)) {
        const issueMessage = issues
          .map((issue: any) =>
            issue?.constraints
              ? Object.values(issue.constraints).join(", ")
              : issue?.message
          )
          .filter(Boolean)
          .join(", ");
        toast("error", issueMessage || "Business status update failed");
      } else {
        const raw =
          err?.data?.message ??
          err?.data?.error ??
          err?.data?.errors ??
          err?.error ??
          err?.message;
        const message =
          typeof raw === "object" && raw !== null
            ? JSON.stringify(raw)
            : raw || "Business status update failed";
        toast("error", message);
      }
    } finally {
      setStatusUpdateId(null);
    }
  };

  const columns: ColumnsType<CompanyRow> = [
    {
      title: "#SL",
      align: "center",
      width: 50,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Id",
      dataIndex: "id",
    },
    {
      title: "Company Name",
      dataIndex: "company_name",
    },
    {
      title: "Email Address",
      dataIndex: "business_email",
    },
    {
      title: "Location",
      dataIndex: "location",
      render: (val) => {
        return val ? capitalize(val) : "N/A";
      },
    },
    {
      title: "Status",
      dataIndex: "id",
      width: 180,
      render: (_val, record) => {
        const isPublished = getPublishedStatus(record);
        return (
          <Space size={8}>
            <Switch
              className="custom-switch"
              checked={isPublished}
              checkedChildren="Published"
              unCheckedChildren="Unpublished"
              onChange={(checked) => handleBusinessPublishStatus(record, checked)}
              loading={statusUpdateId === record?.id && isStatusUpdateLoading}
              disabled={statusUpdateId === record?.id && isStatusUpdateLoading}
            />
          </Space>
        );
      },
    },
    // {
    //   title: "Status",
    //   dataIndex: "status",
    //   align: "center",
    //   render: (value: string) => {
    //     const isTrial = value?.includes("Trial");
    //     const isTrialExpired = value === "Trial Expired";
    //     const displayValue = isTrialExpired ? "Inactive" : value;

    //     return (
    //       <span
    //         className={`px-2 py-1 rounded text-xs font-medium ${
    //           displayValue === "Active"
    //             ? "bg-green-100 text-green-600"
    //             : displayValue === "Inactive"
    //             ? "bg-red-100 text-red-600"
    //             : isTrial
    //             ? "bg-[#F0F3F9] text-[#5E718D]"
    //             : "bg-yellow-100 text-yellow-600"
    //         }`}
    //       >
    //         {displayValue || "N/A"}
    //       </span>
    //     );
    //   },
    // },
    {
      title: "Action",
      dataIndex: "key",
      align: "center",
      render: (val) => (
        <Space size={8}>
          <Link to={`/admin/businesses/business-list/${val}`}>
            <Tooltip title="View Company">
              <Button
                icon={<ViewIcon />}
                size="small"
                className="action-button"
                danger
              />
            </Tooltip>
          </Link>

          <Link to={`/admin/businesses/business-list/edit/${val}`}>
            <Tooltip title="Edit Company">
              <Button
                className="action-button"
                size="small"
                icon={<EditIcon />}
              />
            </Tooltip>
          </Link>

          {/* <Link to={`/admin/businesses/business-list/product-assign/${val}`}>
            <Tooltip title="Assign Product">
              <Button
                className="action-button"
                size="small"
                icon={<AddIcon />}
              />
            </Tooltip>
          </Link> */}
        </Space>
      ),
    },
  ];

  return (
    <SidebarLayout title="Businesses List">
      <CustomTable
        loading={isLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.totalCount}
        // dataSource={addKeyInArray(data?.data?.companies)}
        dataSource={addKeyInArray(
          Array.isArray(data?.data?.companies) ? data?.data?.companies : []
        )}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        hideRowSelect
        filterSection={
          <>
            <Input
              className="w-full !min-w-[180px] max-w-full"
              suffix={<SearchIcon />}
              placeholder="Search by Name, Email"
              allowClear
              onChange={handleSearch}
            />
            <Select
              allowClear
              placeholder="Status"
              className="min-w-[140px]"
              options={[
                { label: "Published", value: 1 },
                { label: "Unpublished", value: 0 },
              ]}
              onChange={(value) =>
                handleFilter(
                  "is_published",
                  value as GetAllCompanyDataParamsType["is_published"]
                )
              }
            />

            {/* <Input suffix={<SearchIcon />} placeholder="Search by Name, Id" />

            <Select
              // defaultValue="lucy"
              style={{ width: 120 }}
              allowClear
              suffixIcon={<SelectDownIcon />}
              options={[
                { value: "Active", label: "Active" },
                { value: "Inactive", label: "Inactive" },
                { value: "Review", label: "Review" },
                { value: "Trial", label: "Trial" },
              ]}
              placeholder="Status"
            />

            <Select
              // defaultValue="lucy"
              className="w-[120px]"
              allowClear
              suffixIcon={<SelectDownIcon />}
              options={[
                { value: "Active", label: "Active" },
                { value: "Inactive", label: "Inactive" },
                { value: "Review", label: "Review" },
                { value: "Trial", label: "Trial" },
              ]}
              placeholder="digiGo"
            /> */}

            {role === "SUPER_ADMIN" ? (
              <Link to="/admin/businesses/business-list/add-new">
                <Button
                  className="middle-button"
                  type="primary"
                  icon={<AddIcon />}
                >
                  Add New
                </Button>
              </Link>
            ) : null}
          </>
        }
      />
    </SidebarLayout>
  );
}
